import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, MoveRight, Building2, CheckCircle2, Landmark, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import SectionRail from "@/components/SectionRail";

const IMG_HERO =
  "https://images.unsplash.com/photo-1703861624479-2b4b5ca7f2f9?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85";
const IMG_A =
  "https://images.unsplash.com/photo-1628860839744-47e547bdc73d?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85";
const IMG_B =
  "https://images.unsplash.com/photo-1631187704662-0c27e423eb9e?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85";
const IMG_C =
  "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85";

const reveal = {
  initial: { opacity: 0, y: 14 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
};

export default function Home() {
  const { t } = useTranslation();
  const { lang = "hr" } = useParams();
  const navigate = useNavigate();

  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [errors, setErrors] = useState({});

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const submit = (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.name.trim()) errs.name = t("contact.validation.name_required");
    if (!form.email.trim()) errs.email = t("contact.validation.email_required");
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = t("contact.validation.email_invalid");
    if (!form.message.trim()) errs.message = t("contact.validation.message_required");
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    navigate("/thank_you");
  };

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 hero-wash pointer-events-none" aria-hidden="true" />
        <div className="relative max-w-[1120px] mx-auto px-4 sm:px-6 lg:px-8 pt-14 sm:pt-20 lg:pt-28 pb-16 sm:pb-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            <motion.div {...reveal} className="lg:col-span-7">
              <div className="eyebrow" data-testid="hero-eyebrow">
                {t("hero.eyebrow")}
              </div>
              <h1
                className="mt-4 font-display text-4xl sm:text-5xl lg:text-[64px] leading-[1.03] tracking-[-0.02em] text-foreground"
                data-testid="hero-headline"
              >
                <span className="block">{t("hero.headline_line1")}</span>
                <span className="block">{t("hero.headline_line2")}</span>
                <span className="block italic text-primary">
                  {t("hero.headline_line3")}
                </span>
              </h1>
              <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-[560px] leading-relaxed">
                {t("hero.subhead")}
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Button
                  type="button"
                  onClick={() => scrollTo("contact")}
                  className="h-11 rounded-lg px-5 tracking-[0.06em]"
                  data-testid="hero-primary-cta"
                >
                  {t("hero.cta_primary")}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => scrollTo("how")}
                  className="h-11 rounded-lg px-5 tracking-[0.06em]"
                  data-testid="hero-secondary-cta"
                >
                  {t("hero.cta_secondary")}
                </Button>
              </div>

              <div className="mt-10 flex flex-wrap gap-2">
                <Badge variant="outline" className="rounded-full border-border/70 px-3 py-1 text-[0.68rem] tracking-[0.14em] uppercase font-mono-plex">
                  HANFA
                </Badge>
                <Badge variant="outline" className="rounded-full border-border/70 px-3 py-1 text-[0.68rem] tracking-[0.14em] uppercase font-mono-plex">
                  GDPR
                </Badge>
                <Badge variant="outline" className="rounded-full border-border/70 px-3 py-1 text-[0.68rem] tracking-[0.14em] uppercase font-mono-plex">
                  Croatia · EUR
                </Badge>
              </div>
            </motion.div>

            <motion.div {...reveal} className="lg:col-span-5">
              <div className="relative rounded-2xl overflow-hidden border border-border/70">
                <img
                  src={IMG_HERO}
                  alt="Croatian residential building"
                  loading="eager"
                  className="w-full h-[380px] sm:h-[460px] lg:h-[520px] object-cover saturate-[0.92] contrast-[1.03]"
                />
                <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/40 via-transparent to-transparent">
                  <div className="text-[0.68rem] font-mono-plex tracking-[0.14em] uppercase text-white/90">
                    {t("hero.image_caption")}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        <div className="max-w-[1120px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="divider-band" />
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" className="scroll-mt-24 py-16 sm:py-20 lg:py-24">
        <div className="max-w-[1120px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            <div className="lg:col-span-2">
              <SectionRail index={t("about.index")} label={t("about.eyebrow")} />
            </div>
            <motion.div {...reveal} className="lg:col-span-10">
              <div className="eyebrow lg:hidden mb-2">{t("about.eyebrow")}</div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-[-0.01em] text-foreground max-w-[880px]">
                {t("about.title")}
              </h2>

              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-4 text-[0.98rem] leading-relaxed text-foreground/90">
                  <p>{t("about.p1")}</p>
                  <p>{t("about.p2")}</p>
                </div>
                <div className="space-y-4 text-[0.98rem] leading-relaxed text-foreground/90">
                  <p>{t("about.p3")}</p>
                  <p>{t("about.p4")}</p>
                </div>
              </div>

              <div className="mt-10 border-l-2 border-primary/40 pl-5">
                <p className="font-display italic text-2xl sm:text-3xl text-foreground/90 max-w-[720px] leading-[1.25]">
                  {t("about.pull_quote")}
                </p>
              </div>

              <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  { src: IMG_A, cap: t("about.image_captions.a") },
                  { src: IMG_B, cap: t("about.image_captions.b") },
                  { src: IMG_C, cap: t("about.image_captions.c") },
                ].map((img, idx) => (
                  <figure key={idx} className="space-y-2">
                    <div className="rounded-xl overflow-hidden border border-border/70">
                      <img
                        src={img.src}
                        alt={img.cap}
                        loading="lazy"
                        className="w-full h-56 object-cover saturate-[0.9] contrast-[1.03]"
                      />
                    </div>
                    <figcaption className="text-[0.7rem] font-mono-plex tracking-[0.14em] uppercase text-muted-foreground">
                      {img.cap}
                    </figcaption>
                  </figure>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="scroll-mt-24 py-16 sm:py-20 lg:py-24 bg-accent/40">
        <div className="max-w-[1120px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            <div className="lg:col-span-2">
              <SectionRail index={t("how.index")} label={t("how.eyebrow")} />
            </div>
            <motion.div {...reveal} className="lg:col-span-10">
              <div className="eyebrow lg:hidden mb-2">{t("how.eyebrow")}</div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-[-0.01em] text-foreground max-w-[720px]">
                {t("how.title")}
              </h2>
              <p className="mt-5 text-base sm:text-lg text-muted-foreground max-w-[720px] leading-relaxed">
                {t("how.intro")}
              </p>

              <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-5">
                {[1, 2, 3, 4].map((n) => (
                  <div
                    key={n}
                    className="relative bg-card border border-border/70 rounded-xl p-6"
                    data-testid={`how-step-${n}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <span className="font-mono-plex text-xs tracking-[0.14em] text-muted-foreground uppercase">
                        {t(`how.steps.${n}.num`)}
                      </span>
                      <span className="h-8 w-8 rounded-full border border-primary/30 flex items-center justify-center text-primary">
                        <MoveRight className="h-4 w-4" />
                      </span>
                    </div>
                    <h3 className="mt-4 font-display text-xl sm:text-2xl tracking-[-0.01em] text-foreground">
                      {t(`how.steps.${n}.title`)}
                    </h3>
                    <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                      {t(`how.steps.${n}.desc`)}
                    </p>
                  </div>
                ))}
              </div>

              <p className="mt-10 text-sm text-muted-foreground max-w-[720px] leading-relaxed">
                {t("how.note")}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* TRUST / TRANSPARENCY */}
      <section id="trust" className="scroll-mt-24 py-16 sm:py-20 lg:py-24">
        <div className="max-w-[1120px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            <div className="lg:col-span-2">
              <SectionRail index={t("trust.index")} label={t("trust.eyebrow")} />
            </div>
            <motion.div {...reveal} className="lg:col-span-10">
              <div className="eyebrow lg:hidden mb-2">{t("trust.eyebrow")}</div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-[-0.01em] text-foreground max-w-[880px]">
                {t("trust.title")}
              </h2>
              <p className="mt-5 text-base sm:text-lg text-muted-foreground max-w-[820px] leading-relaxed">
                {t("trust.body")}
              </p>

              <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-10">
                <div>
                  <h3 className="font-mono-plex text-[0.72rem] tracking-[0.14em] uppercase text-muted-foreground">
                    {t("trust.principles_title")}
                  </h3>
                  <ul className="mt-4 space-y-4">
                    {[1, 2, 3, 4].map((n) => (
                      <li key={n} className="flex items-start gap-3">
                        <span className="mt-1 text-primary">
                          <CheckCircle2 className="h-5 w-5" />
                        </span>
                        <div>
                          <div className="text-base font-medium text-foreground">
                            {t(`trust.principles.${n}.title`)}
                          </div>
                          <div className="text-sm text-muted-foreground mt-0.5">
                            {t(`trust.principles.${n}.desc`)}
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="bg-card border border-border/70 rounded-2xl p-6 sm:p-8">
                  <div className="flex items-center gap-3">
                    <ShieldCheck className="h-6 w-6 text-primary" />
                    <span className="font-mono-plex text-[0.72rem] tracking-[0.14em] uppercase text-muted-foreground">
                      Arqovena
                    </span>
                  </div>
                  <p className="mt-4 font-display text-2xl sm:text-3xl leading-[1.2] tracking-[-0.01em] text-foreground">
                    {t("trust.mission")}
                  </p>
                  <div className="mt-6 flex items-center gap-3 text-sm text-muted-foreground">
                    <Landmark className="h-4 w-4" />
                    <span>{t("footer.legal.supervision")}</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="scroll-mt-24 py-16 sm:py-20 lg:py-24 bg-accent/40">
        <div className="max-w-[1120px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            <div className="lg:col-span-2">
              <SectionRail index={t("contact.index")} label={t("contact.eyebrow")} />
            </div>
            <motion.div {...reveal} className="lg:col-span-10">
              <div className="eyebrow lg:hidden mb-2">{t("contact.eyebrow")}</div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-[-0.01em] text-foreground max-w-[720px]">
                {t("contact.title")}
              </h2>
              <p className="mt-4 text-base text-muted-foreground max-w-[640px]">
                {t("contact.subhead")}
              </p>

              <form
                onSubmit={submit}
                className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5 max-w-[820px]"
                data-testid="contact-form"
                noValidate
              >
                <div className="md:col-span-1">
                  <Label htmlFor="name" className="text-xs font-mono-plex tracking-[0.14em] uppercase text-muted-foreground">
                    {t("contact.name_label")}
                  </Label>
                  <Input
                    id="name"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder={t("contact.name_placeholder")}
                    className="mt-2 h-11 rounded-lg bg-card"
                    data-testid="contact-name-input"
                    aria-invalid={!!errors.name}
                  />
                  {errors.name && (
                    <p className="mt-1 text-xs text-destructive" data-testid="contact-name-error">{errors.name}</p>
                  )}
                </div>
                <div className="md:col-span-1">
                  <Label htmlFor="email" className="text-xs font-mono-plex tracking-[0.14em] uppercase text-muted-foreground">
                    {t("contact.email_label")}
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                    placeholder={t("contact.email_placeholder")}
                    className="mt-2 h-11 rounded-lg bg-card"
                    data-testid="contact-email-input"
                    aria-invalid={!!errors.email}
                  />
                  {errors.email && (
                    <p className="mt-1 text-xs text-destructive" data-testid="contact-email-error">{errors.email}</p>
                  )}
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="message" className="text-xs font-mono-plex tracking-[0.14em] uppercase text-muted-foreground">
                    {t("contact.message_label")}
                  </Label>
                  <Textarea
                    id="message"
                    value={form.message}
                    onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                    placeholder={t("contact.message_placeholder")}
                    className="mt-2 min-h-[140px] rounded-lg bg-card"
                    data-testid="contact-message-textarea"
                    aria-invalid={!!errors.message}
                  />
                  {errors.message && (
                    <p className="mt-1 text-xs text-destructive" data-testid="contact-message-error">{errors.message}</p>
                  )}
                </div>
                <div className="md:col-span-2 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
                  <p className="text-xs text-muted-foreground max-w-[520px]">
                    {t("contact.legal_note")}
                  </p>
                  <Button
                    type="submit"
                    className="h-11 rounded-lg px-6 tracking-[0.06em]"
                    data-testid="contact-submit-button"
                  >
                    {t("contact.submit")}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </form>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
